import React, {useEffect, useMemo, useState} from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Input,
    InputGroup,
    InputGroupText,
} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import TableUser from "./components/TableTeam";
import NoSearchResult from "../../components/Common/NoSearchResult";
import ModalUser from "./components/Modal";
import {getUsers, modalUser, clearUsers} from "../../store/user/actions";
import usePermissions from "src/hooks/usePermissions";
import { getRoles } from "src/store/actions";
import Select from "src/components/Common/Select";

const Users = () => {
    document.title = "User List | Dashboard";
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {hasPermission} = usePermissions();

    const data = useSelector((state: any) => state.Users.data || []);
    const loading = useSelector((state: any) => state.Users.loading || false);
    const isOpenModal = useSelector((state: any) => state.Users.isOpenModal || false);
    const roles = useSelector((state: any) => state.roles.data || []);

    const [selectedRoles, setSelectedRoles] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        dispatch(getRoles())
        return () => {
            dispatch(clearUsers())
        };
    }, [dispatch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleUserClicks = () => {
        dispatch(modalUser(true))
    };

    const roleOptions = useMemo(() => (
        (Array.isArray(roles) ? roles : []).map((item: any) => ({
            value: item?.id,
            label: item?.name,
        })).filter((item: any) => item.value != null)
    ), [roles]);

    const users = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.data)) return data.data;
        return [];
    }, [data]);

    const roleIds = useMemo(
        () => selectedRoles.map((item: any) => item?.value).filter((value: any) => value != null),
        [selectedRoles]
    );

    useEffect(() => {
        const filter: any = { per_page: 1000 };
        if (debouncedSearch) {
            filter.search = debouncedSearch;
        }
        if (roleIds.length) {
            filter.role_ids = roleIds.join(",");
        }
        dispatch(getUsers("", filter));
    }, [dispatch, debouncedSearch, roleIds]);

    const filteredUsers = useMemo(() => users, [users]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem="Users" />

                    <Row className="align-items-center">
                        <Col md={12}>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <div style={{minWidth: 320}}>
                                        <InputGroup>
                                            <InputGroupText>
                                                <i className="bx bx-search-alt-2"></i>
                                            </InputGroupText>
                                            <Input
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="Search by name, email, team, role or ID"
                                                aria-label="Search users"
                                            />
                                            {searchTerm ? (
                                                <InputGroupText
                                                    role="button"
                                                    onClick={() => setSearchTerm("")}
                                                    title="Clear search"
                                                    style={{cursor: "pointer"}}
                                                >
                                                    <i className="bx bx-x"></i>
                                                </InputGroupText>
                                            ) : null}
                                        </InputGroup>
                                    </div>
                                    <div style={{minWidth: 260}}>
                                        <Select
                                            isMulti
                                            isSearchable
                                            isClearable
                                            placeholder="Filter by roles"
                                            options={roleOptions}
                                            onChange={(value: any) => setSelectedRoles(value || [])}
                                            value={selectedRoles}
                                        />
                                    </div>
                                </div>
                                <div>
                                    {
                                    hasPermission("create-users") &&  
                                            <Link to="#" className="btn btn-light" onClick={handleUserClicks}>
                                                <i className="bx bx-plus me-1"></i> Add New
                                            </Link>
                                    }
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody className="border-bottom">
                                    { filteredUsers.length > 0 && <TableUser data={filteredUsers} pageSize={20}/> }
                                    { !loading && filteredUsers.length === 0 ? ( <NoSearchResult /> ) : null }
                                    { loading ? ( <NoSearchResult text="Loading..." /> ) : null }
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                </Container>

                {isOpenModal && <ModalUser isOpen={isOpenModal} />}
            </div>
        </React.Fragment>
    );
};

export default Users;
